<script lang="ts">
  import { onMount, onDestroy } from "svelte";
  import { Wifi, WifiOff, RefreshCw, CheckCircle2, ShieldAlert } from "lucide-svelte";

  let isOnline = $state(typeof navigator !== "undefined" ? navigator.onLine : true);
  let showReconnectedNotice = $state(false);
  let pendingSyncCount = $state(0);
  let reconnectedTimer: any = null;

  function countPendingOfflineActions() {
    if (typeof window === "undefined" || !window.localStorage) return;
    try {
      let count = 0;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith("field_tech_offline_actions_") || key.startsWith("turnover_inspection_offline_"))) {
          const item = localStorage.getItem(key);
          if (item) {
            const parsed = JSON.parse(item);
            if (Array.isArray(parsed)) {
              count += parsed.length;
            }
          }
        }
      }
      pendingSyncCount = count;
    } catch {
      // ignore
    }
  }

  function handleOnline() {
    isOnline = true;
    showReconnectedNotice = true;
    countPendingOfflineActions();
    if (reconnectedTimer) clearTimeout(reconnectedTimer);
    reconnectedTimer = setTimeout(() => {
      showReconnectedNotice = false;
    }, 4000);
  }

  function handleOffline() {
    isOnline = false;
    showReconnectedNotice = false;
    countPendingOfflineActions();
  }

  onMount(() => {
    if (typeof window !== "undefined") {
      window.addEventListener("online", handleOnline);
      window.addEventListener("offline", handleOffline);
      countPendingOfflineActions();
    }
  });

  onDestroy(() => {
    if (typeof window !== "undefined") {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    }
    if (reconnectedTimer) clearTimeout(reconnectedTimer);
  });
</script>

{#if !isOnline}
  <div 
    class="fixed bottom-4 right-4 z-50 flex items-center gap-2.5 px-3.5 py-2 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 text-xs font-medium shadow-lg backdrop-blur-md animate-in fade-in slide-in-from-bottom-2 duration-200"
    role="status"
    aria-live="polite"
    data-testid="network-offline-badge"
  >
    <span class="relative flex h-2 w-2">
      <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
      <span class="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
    </span>
    <WifiOff class="w-3.5 h-3.5 text-amber-400 shrink-0" />
    <span>Basement Offline Mode</span>
    {#if pendingSyncCount > 0}
      <span class="bg-amber-500/25 px-1.5 py-0.5 rounded text-[10px] font-semibold text-amber-200">
        {pendingSyncCount} queued
      </span>
    {/if}
  </div>
{:else if showReconnectedNotice}
  <div 
    class="fixed bottom-4 right-4 z-50 flex items-center gap-2 px-3.5 py-2 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs font-medium shadow-lg backdrop-blur-md animate-in fade-in slide-in-from-bottom-2 duration-200"
    role="status"
    aria-live="polite"
    data-testid="network-online-badge"
  >
    <CheckCircle2 class="w-3.5 h-3.5 text-emerald-400 shrink-0" />
    <span>Back Online — All Systems Synced</span>
  </div>
{/if}
